import { captureException } from '@sentry/nuxt';
import { Code, ConnectError } from '@connectrpc/connect';
import useKeycloakAuth from './auth/useKeycloakAuth';
import { GP_ACCESS_TOKEN_COOKIE } from './auth/constants';

export default defineNuxtPlugin({
  dependsOn: ['grpc-transport'],
  setup: async () => {
    const { $i18n } = useNuxtApp();

    const authStore = useAuthStore();
    const oidcStore = useOidcStore();
    const notificationStore = useNotificationsStore();

    const route = useRoute();
    const router = useRouter();

    const cleanUrl = () => {
      setTimeout(() => {
        // Don't strip the `state` param on the checkout result page —
        // it's a Chargebee checkout state (succeeded/cancelled), not a Keycloak OAuth state.
        const isCheckoutResult = route.path.includes('/checkout/result');

        const currentQuery = { ...route.query };
        if (!isCheckoutResult) {
          delete currentQuery.state;
        }
        delete currentQuery.code;
        delete currentQuery.session_state;
        delete currentQuery.iss;
        router.replace({ query: currentQuery });
      }, 1000);
    };
    const { auth } = await useKeycloakAuth(cleanUrl);

    const tokenCookie = useCookie(GP_ACCESS_TOKEN_COOKIE);

    // Reliable SSR→client transport for the tokens written during the PKCE exchange.
    // Pinia v3 setup-store hydration can silently fail for reactive() + toRefs() stores
    // (oidcStore), leaving refreshToken null on the client. useState() is serialised
    // directly into the Nuxt __nuxt_data payload and is always available on first render.
    const ssrTokens = useState<{
      refreshToken?: string;
      accessToken?: string;
      idToken?: string;
    }>('auth:ssr-tokens', () => ({}));

    // The full path to return to after login, query included, when the callback
    // URL lost its query (see redirectUriPath()). Set by the server plugin.
    const authReturnTo = useState<string | null>('auth:return-to', () => null);

    /**
     * When coming from the login callback, we have to handle the auth callback and init the keycloak-js with tokens for token refresh mechanism.
     *
     * @returns {Promise<{ provide: { auth: KeycloakAuth | null } }>}
     */
    const handleAuthCallback = async () => {
      // Prefer Pinia store values; fall back to the useState() transport set by the
      // server plugin in case Pinia hydration did not propagate the refresh token.
      const token =
        oidcStore.accessToken ||
        ssrTokens.value?.accessToken ||
        tokenCookie.value ||
        undefined;
      const refreshToken =
        oidcStore.refreshToken || ssrTokens.value?.refreshToken || undefined;
      const idToken =
        oidcStore.idToken || ssrTokens.value?.idToken || undefined;

      // Clear the useState payload after consuming it — it contains tokens and
      // should not persist beyond the first render.
      ssrTokens.value = {};

      // Do NOT pass onLoad: 'check-sso' here.
      // keycloak-js v26 with checkLoginIframe:false and no silentCheckSsoRedirectUri
      // performs a full-page redirect to Keycloak for check-sso.  When this redirect
      // returns the token endpoint POST is CORS-blocked (origin not in Keycloak
      // Web Origins), causing 5 retries then forceLogout().
      // We already have valid tokens from the server-side PKCE exchange — passing
      // them here (fast-set path) is enough to initialise the refresh mechanism
      // without any network round-trip to Keycloak.
      await auth.init({
        token,
        idToken,
        refreshToken,
      });

      // Only now that init() has stored the session. A full load rather than a
      // router.replace(): pages read their query once, in setup (the payments
      // page's ?amount= pre-fill).
      const returnTo = authReturnTo.value;
      authReturnTo.value = null;
      if (returnTo != null) {
        window.location.replace(returnTo);
      }

      return {
        provide: {
          auth,
        },
      };
    };

    /**
     * When we are authenticated on server side, also init auth for establishing the session on frontend side.
     *
     * @returns {Promise<{ provide: { auth: KeycloakAuth | null } }>}
     */
    const handleExistingSession = async () => {
      // Safety net: if we are still on the auth-callback URL (OAuth code in query) but
      // ended up in handleExistingSession instead of handleAuthCallback, do NOT call
      // auth.init() without tokens.  In @gportal/keycloak-auth v1.3.1 calling init()
      // without both token+refreshToken triggers clearTokenStorage() which deletes the
      // gp_at cookie, followed by a keycloak-js check-sso full-page redirect — the page
      // then reloads without gp_at and SSR renders the user as anonymous (logged out).
      if (route.query.code != null) {
        cleanUrl();
        return {
          provide: {
            auth,
          },
        };
      }

      // Fast path: localStorage already has a token set from a previous session.
      // auth.init({}) is safe here — the library fast-paths (no initKeycloak call,
      // no Keycloak network round-trip) and crucially calls startTickInterval(),
      // which starts the proactive token auto-refresh loop.
      // Without this path the guard below would fire on every normal page reload,
      // preventing startTickInterval() from ever running and causing the access token
      // to expire silently after its TTL (~10 min) with nothing to refresh it.
      if (auth.storedTokenSet != null) {
        await auth.init({});
        return {
          provide: {
            auth,
          },
        };
      }

      // Guard: localStorage is empty. If SSR established a valid session
      // (isAuthenticated=true), skip init() to preserve the server-set gp_at cookie.
      // Calling auth.init({}) with no storedTokenSet triggers clearTokenStorage() which
      // deletes gp_at via document.cookie — logging the user out immediately.
      // We key off authStore.isAuthenticated (not tokenCookie.value) deliberately:
      // isAuthenticated=true means SSR just succeeded with a valid token; on /nuxt/callback
      // after token expiry, SSR sets isAuthenticated=false in the UNAUTHENTICATED catch —
      // so the guard doesn't fire there, init() runs, and the stale cookie gets cleared
      // correctly before the login redirect.
      if (
        authStore.isAuthenticated &&
        oidcStore.refreshToken == null &&
        ssrTokens.value?.refreshToken == null
      ) {
        return {
          provide: {
            auth,
          },
        };
      }

      await auth.init({});

      return {
        provide: {
          auth,
        },
      };
    };

    /**
     * When we have a server error, then show the error notification on the frontend side. No more actions are necessary.
     */
    const handleServiceError = async () => {
      if (route.query.service_error != 'null') {
        notificationStore.pushNotification({
          type: 'error',
          title: $i18n.t('general/error/text'),
          message: `${$i18n.t(
            'frontend/something-went-wrong-please-try-again-later',
          )} | ${route.query.service_error}`,
          id: new Date().getTime(),
        });
      }

      cleanUrl();
    };

    try {
      if (
        // if the server provided the access token (from auth callback), init keycloak-js for token refresh mechanism.
        // Check both Pinia store and the useState() fallback — Pinia v3 setup-store
        // hydration can silently fail leaving oidcStore.refreshToken null on the client.
        authStore.isAuthenticated &&
        (oidcStore.refreshToken != null ||
          ssrTokens.value?.refreshToken != null)
      ) {
        return handleAuthCallback();
      }

      if (
        // if we are authenticated on server side, also init auth for establishing the session on frontend side
        authStore.isAuthenticated ||
        // or if we have cookies, then we are also authenticated on server side
        tokenCookie.value != null ||
        // or localStorage has a stored token set — recover the session using the refresh token even
        // when gp_at is absent (e.g. browser was closed, wiping the session cookie before the
        // library had a chance to set the persistent cookie, or the cookie was cleared externally).
        // handleExistingSession → auth.storedTokenSet fast-path → auth.init({}) → startTickInterval()
        // → first tick detects expired access_token_exp → initKeycloak → KC JS updateToken(-1) →
        // new tokens → gp_at restored. If the refresh token is also expired, init() propagates the
        // error to our catch block, which calls logout() to clear stale state cleanly.
        auth.storedTokenSet != null ||
        // or server has returned a GRPC 16 service error (unauthorized), which indicates in most cases that the token is expired
        // try to recover the session on frontend side
        (authStore.serviceError != null &&
          (authStore.serviceError as unknown as ConnectError).code ===
            Code.Unauthenticated)
      ) {
        return handleExistingSession();
      }

      if (route.query.service_error != null) {
        // in case the error not a 'null' string, then show the error notification
        handleServiceError();
      } else if (route.fullPath === '/nuxt/callback') {
        // in case we are at the callback page, but we do not have a service error, destroy the session and redirect to the redirect query param
        authStore.user = null;
      }
    } catch (error) {
      captureException(error);

      // if there is something wrong with the auth, then destroy the session and redirect to the home page
      const { logout } = await useKeycloakAuth(cleanUrl);
      await logout();
    }

    return {
      provide: {
        auth,
      },
    };
  },
});
